import React, { useEffect, useRef, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { Box, Flex, Text } from 'components/ui';
import { wrapFunctionalComponent } from 'utils/framework';
import { fontSizes } from 'style/config';
import localeUtils from 'utils/LanguageLocale';
import PRSSummaryEN from 'ai/components/PRSSummary/locales/PRSSummary_en_US';
import PRSSummaryFR from 'ai/components/PRSSummary/locales/PRSSummary_fr_CA';
import { productSelector } from 'selectors/page/product/productSelector';
import AspectChips from 'ai/components/PRSSummary/AspectChips';
import { trackPRSSummaryImpression, trackErrorOrEmptyState } from 'ai/analytics/trackers/prsTrackers';

const getText = localeUtils.getLocalisedDataFromResource(PRSSummaryEN, PRSSummaryFR);

function renderHighlightedSummary(summary, highlights) {
    if (!highlights || !highlights.length) {
        return summary;
    }

    const parts = [];
    let lastIndex = 0;

    const sorted = [...highlights].sort((a, b) => a.startIndex - b.startIndex);

    sorted.forEach(({ startIndex, endIndex }) => {
        if (startIndex > lastIndex) {
            parts.push(summary.slice(lastIndex, startIndex));
        }

        parts.push(<strong key={startIndex}>{summary.slice(startIndex, endIndex)}</strong>);
        lastIndex = endIndex;
    });

    if (lastIndex < summary.length) {
        parts.push(summary.slice(lastIndex));
    }

    return parts;
}

const trackedImpressions = new Set();
const trackedEmptyStates = new Set();

function PRSSummary() {
    const product = useSelector(productSelector);
    const sentimentsSummary = product?.sentimentsSummary;
    const containerRef = useRef(null);
    const productId = product?.productDetails?.productId;

    const handleImpression = useCallback(() => {
        if (!trackedImpressions.has(productId)) {
            trackedImpressions.add(productId);
            trackPRSSummaryImpression({ productId });
        }
    }, [productId]);

    useEffect(() => {
        const node = containerRef.current;

        if (!node || !sentimentsSummary?.summary) {
            return undefined;
        }

        if (typeof window === 'undefined' || !('IntersectionObserver' in window)) {
            handleImpression();

            return undefined;
        }

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    handleImpression();
                    observer.disconnect();
                }
            },
            { threshold: 0.5 }
        );

        observer.observe(node);

        return () => observer.disconnect();
    }, [sentimentsSummary, handleImpression]);

    useEffect(() => {
        if (productId && !trackedEmptyStates.has(productId) && (!sentimentsSummary || !sentimentsSummary.summary)) {
            trackedEmptyStates.add(productId);
            trackErrorOrEmptyState({ productId, errorType: 'empty' });
        }
    }, [productId, sentimentsSummary]);

    if (!sentimentsSummary || !sentimentsSummary.summary) {
        return null;
    }

    const { summary, summaryHighLights } = sentimentsSummary;
    const sentiments = product?.sentiments;

    return (
        <Flex
            ref={containerRef}
            flexDirection='column'
            gap={4}
            data-at={Sephora.debug.dataAt('prs_summary_module')}
        >
            <Text
                is='p'
                css={styles.heading}
                lineHeight='normal'
            >
                {renderHighlightedSummary(summary, summaryHighLights)}
            </Text>

            <Box>
                <Text
                    is='p'
                    color='gray'
                    lineHeight='normal'
                    children={getText('aiDisclaimer')}
                    css={styles.aiDisclaimer}
                />

                {sentimentsSummary.hasIncentivizedReviews && (
                    <Text
                        is='p'
                        color='gray'
                        lineHeight='normal'
                        marginTop={1}
                        css={styles.aiDisclaimer}
                        children={getText('incentivizedNote')}
                    />
                )}
            </Box>

            {sentiments && sentiments.length > 0 && <AspectChips />}
        </Flex>
    );
}

const styles = {
    heading: {
        fontSize: fontSizes.base
    },
    aiDisclaimer: {
        fontSize: fontSizes.sm
    }
};

export default wrapFunctionalComponent(PRSSummary, 'PRSSummary');
